import * as Yup from 'yup';
import { parse, isBefore, isEqual, startOfToday } from 'date-fns';

export const createEventValidationSchema = (t, edit = false) => {

    const timeValidator = (values) => {
        const { startDate, endDate, startTime, endTime } = values;
        if (!startDate || !endDate || !startTime || !endTime) return true;
        const isSameDate = isEqual(new Date(startDate).setHours(0, 0, 0, 0), new Date(endDate).setHours(0, 0, 0, 0));
        if (isSameDate) {
            const start = parse(startTime, 'HH:mm', new Date());
            const end = parse(endTime, 'HH:mm', new Date());
            return isBefore(start, end);
        }
        return true;
    };

    const isNotInPast = (value) => {
        if (edit) return true;
        if (!value) return false;
        const selectedDate = new Date(value);
        return selectedDate >= startOfToday();
    };

    return (Yup.object().shape({
        title: Yup.string().required(t('required.message')).max(100, t('max.title.message')),
        address: Yup.string().max(200, t('max.address.message'))
            .when('eventType', {
                is: 'offline',
                then: (schema) =>
                    schema
                        .typeError(t('required.message'))
                        .required(t('required.message')),
                otherwise: (schema) =>
                    schema.notRequired().nullable(true)
            }),
        url: Yup.string().url(t('url.wrong', 'Niepoprawny format linku')).max(500, t('max.url.message', 'Link może mieć maksymalnie 500 znaków'))
            .when('eventType', {
                is: 'online',
                then: (schema) =>
                    schema
                        .typeError(t('required.message'))
                        .required(t('required.message')),
                otherwise: (schema) =>
                    schema.notRequired().nullable(true)
            }),
        platform: Yup.string().max(100, t('max.platform.message', 'Nazwa platformy może mieć maksymalnie 100 znaków'))
            .when('eventType', {
                is: 'online',
                then: (schema) =>
                    schema
                        .typeError(t('required.message'))
                        .required(t('required.message')),
                otherwise: (schema) =>
                    schema.notRequired().nullable(true)
            }),
        directions: Yup.string(),
        startDate: Yup.string().required(t('required.message')).test('is-not-in-past', t('min.startDate.message'), isNotInPast),
        startTime: Yup.string().required(t('required.message'))
            .test('is-not-in-past', t('min.startDate.message'), function (value) {
                if (edit) return true;
                const { startDate } = this.parent;
                if (!startDate || !value) return true;

                const now = new Date();
                const selectedDateTime = parse(`${startDate} ${value}`, 'yyyy-MM-dd HH:mm', new Date());

                return !isBefore(selectedDateTime, now);
            }),
        endDate: Yup.string().required(t('required.message'))
            .test('is-not-in-past', t('min.endDate.message'), isNotInPast)
            .test(
                'is-after-start-date',
                t('is-after-start-date.message'),
                function (endDate) {
                    const { startDate } = this.parent;
                    return !startDate || !endDate || endDate >= startDate;
                }
            ),
        endTime: Yup.string().required(t('required.message'))
            .test(
                'is-after-start-time',
                t('is-after-start-time.message'),
                function (endTimeStr) {
                    const { startTime, startDate, endDate } = this.parent;
                    if (startDate !== endDate) return true;

                    const [startH, startM] = (startTime ?? '').split(':').map(Number);
                    const [endH, endM] = (endTimeStr ?? '').split(':').map(Number);

                    const startMinutes = startH * 60 + startM;
                    const endMinutes = endH * 60 + endM;

                    return endMinutes > startMinutes;
                }
            ),
        maxParticipants: Yup.number()
            .transform((val, orig) => (orig === '' || orig == null ? undefined : val))
            .when('ticketType', {
                is: 'free',
                then: (schema) =>
                    schema
                        .typeError(t('required.message'))
                        .positive()
                        .integer()
                        .required(t('required.message'))
                        .moreThan(0, t('more-than.maxParticipants')),
                otherwise: (schema) =>
                    schema.notRequired().nullable(true)
            }),
        description: Yup.string(),
        categories: Yup.array().max(3, t('category.error.message')),
        hashtags: Yup.array(),
        eventType: Yup.string().oneOf(['offline', 'online']).required(t('required.message')),
        isFree: Yup.boolean(),
        ticketType: Yup.string().oneOf(['paid', 'free']),
        hostName: Yup.string().required(t('required.message')),
        hostPhonePrefix: Yup.string(),
        hostPhoneNumber: Yup.string(),
        hostEmail: Yup.string().email(t('event.email.wrong')),
        hostInfo: Yup.string().max(2000, t('max.hostInfo.message'))
    }).test('czas-valid', t('validation.time'), timeValidator));
}
